import { useState, useRef, useCallback, useEffect } from 'react';
import { getCampaignId, getToken } from '../api/client.js';
import { addToQueue } from '../offline/syncQueue.js';

const MAX_OCR_FAILURES = 3;
const MIN_CONFIDENCE = 0.95;

/**
 * Hook encapsulating camera stream, capture, OCR logic, and anti-doublon.
 * @param {{ onScanResult: (result: object) => void, offline: boolean, scannedSerials: string[] }} opts
 * @returns {object} Camera state and actions
 */
export default function useScanCamera({ onScanResult, offline, scannedSerials }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [streaming, setStreaming] = useState(false);
  const [captured, setCaptured] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [manualSerial, setManualSerial] = useState('');
  const [error, setError] = useState('');
  const [ocrFailCount, setOcrFailCount] = useState(0);
  const [forceManual, setForceManual] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState('');

  const startCamera = useCallback(async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreaming(true);
      }
    } catch (_mediaErr) {
      setError("Impossible d'accéder à la caméra");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  /** Capture frame without stopping the camera (scan rafale) */
  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    setCaptured(base64);
    setDuplicateWarning('');
    // Camera stays open - no stopCamera() call
  }, []);

  /** Send captured image to OCR endpoint */
  const sendForOCR = useCallback(async () => {
    if (!captured) return;
    setScanning(true);
    setError('');
    setDuplicateWarning('');
    try {
      const campaignId = getCampaignId();
      const response = await fetch(`/api/c/${campaignId}/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
          'X-Campaign-Id': campaignId,
        },
        body: JSON.stringify({ image: captured, mediaType: 'image/jpeg' }),
      });
      if (!response.ok) throw new Error('Erreur OCR');
      const result = await response.json();

      if (result.confidence < MIN_CONFIDENCE && !result.ds3?.match) {
        const newCount = ocrFailCount + 1;
        setOcrFailCount(newCount);
        if (newCount >= MAX_OCR_FAILURES) {
          setForceManual(true);
          setError(`OCR échoué ${MAX_OCR_FAILURES} fois (confiance < 95%). Saisie manuelle requise.`);
        } else {
          setError(`Confiance faible (${Math.round(result.confidence * 100)}%). Tentative ${newCount}/${MAX_OCR_FAILURES}.`);
          setCaptured(null);
        }
        return;
      }

      // DS3 cross-validation succeeded even if OCR confidence was low
      if (result.ds3?.match && result.confidence < MIN_CONFIDENCE) {
        result.confidence = 0.96; // DS3 provides higher trust
        result.method = 'ocr_ds3';
      }

      setOcrResult(result);
      setOcrFailCount(0);
      if (navigator.vibrate) navigator.vibrate([50, 30, 50]); // success feedback
    } catch (_fetchErr) {
      const newCount = ocrFailCount + 1;
      setOcrFailCount(newCount);
      if (newCount >= MAX_OCR_FAILURES) {
        setForceManual(true);
        setError(`OCR échoué ${MAX_OCR_FAILURES} fois. Saisie manuelle requise.`);
      } else {
        setError(`OCR échoué. Tentative ${newCount}/${MAX_OCR_FAILURES}.`);
      }
    } finally {
      setScanning(false);
    }
  }, [captured, ocrFailCount]);

  /** Check for duplicate and confirm result. Queue OCR verify if offline + manual. */
  const confirmResult = useCallback(
    (serial, secondary, method) => {
      const normalized = serial.trim().toUpperCase();

      // Anti-doublon check
      if (scannedSerials.some((s) => s.toUpperCase() === normalized)) {
        setDuplicateWarning(`Deja scanne ! (${normalized})`);
        return;
      }

      // If offline and manual entry with a photo, queue OCR verification for reconnection
      if (offline && (method === 'manual') && captured) {
        const campaignId = getCampaignId();
        addToQueue({
          type: 'ocr_verify',
          url: `/api/c/${campaignId}/scan`,
          method: 'POST',
          body: {
            image: captured,
            mediaType: 'image/jpeg',
            manualSerial: serial,
            campaignId,
          },
        }).catch(() => {}); // Best-effort queue
      }

      onScanResult({
        serialNumber: serial,
        secondaryId: secondary || '',
        method: method || 'manual',
        photoBase64: captured,
      });

      // Reset for next scan (camera stays open)
      setCaptured(null);
      setOcrResult(null);
      setManualSerial('');
      setDuplicateWarning('');
      setForceManual(false);
      setOcrFailCount(0);
    },
    [captured, onScanResult, scannedSerials, offline],
  );

  /** Reset captured image to retake */
  const retake = useCallback(() => {
    setCaptured(null);
    setOcrResult(null);
    setDuplicateWarning('');
  }, []);

  return {
    videoRef,
    canvasRef,
    streaming,
    captured,
    scanning,
    ocrResult,
    manualSerial,
    error,
    forceManual,
    duplicateWarning,
    setManualSerial,
    startCamera,
    stopCamera,
    capture,
    sendForOCR,
    confirmResult,
    retake,
  };
}
